import { type GatewayId, memeApiBaseUrlOf } from '@rb/shared/admin-verses';
import { extractSales, type VxServerItem, type VxUpsertPayload } from '@/shop/vxshop-mapping';

const itemsUrl = (gatewayId: GatewayId) => `${memeApiBaseUrlOf(gatewayId)}/v1/vx-shop-items`;
const salesUrl = (gatewayId: GatewayId) => `${memeApiBaseUrlOf(gatewayId)}/v1/vx-shop-items/sales`;

/** 상품 목록 페이지당 항목 수. */
export const LIST_PAGE_LIMIT = 50;
const LIST_PAGE_DELAY_MS = 400; // 페이지 간 rate-limit 완화
const MAX_LIST_PAGES = 100; // 런어웨이 가드 — 정상 샵은 도달 안 함

/** sales API 페이지당 항목 수. */
export const SALES_PAGE_LIMIT = 50;
const SALES_PAGE_DELAY_MS = 400; // 페이지 간 rate-limit 완화
const MAX_SALES_PAGES = 2000; // 런어웨이 가드
const SALES_MAX_RETRIES = 2; // 429/5xx 일시 오류 시 페이지당 재시도 횟수
const SALES_RETRY_BACKOFF_MS = [1000, 2000]; // 재시도 간 백오프 (1s, 2s)

export interface VxAuth {
  token: string;
  verseId: string;
  gatewayId: GatewayId;
}

/** sales API 레코드 (라이브 확정 스키마). VX→USD = totalPrice / 100. */
export interface VxSale {
  id: number;
  userUid: string; // 구매자 게임 uid (= buyer.uid)
  shopItemId: number;
  quantity: number;
  unitPrice: number; // VX
  totalPrice: number; // VX (= unitPrice × quantity) ← 매출
  status: string; // 'completed' 등
  createdAt: string; // ISO UTC (결제일시)
  refundedAt: string | null; // null = 정상, 값 있으면 환불
  disputeStatus: string | null;
  disputeResolvedAt: string | null;
  stage: string; // 'PROD' | 'STAGING' | 'DEV'
  item: { id: number; productId: string; name: string; imageUrl?: string };
  // displayName = V8 프로필 표시명(닉네임), handle = @핸들, name = 계정 이름(null 인 계정 많음).
  buyer: {
    uid: string;
    walletAddress?: string;
    displayName?: string;
    handle?: string;
    name?: string | null;
  };
}

function headers(token: string): HeadersInit {
  return {
    Accept: '*/*',
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function sleep(ms: number): Promise<void> {
  return ms > 0 ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
}

/** Fetch all shop items via pagination and return a productId → item map. */
export async function listShopItems(auth: VxAuth): Promise<Map<string, VxServerItem>> {
  const map = new Map<string, VxServerItem>();
  const limit = LIST_PAGE_LIMIT;
  for (let page = 1; page < MAX_LIST_PAGES; page++) {
    const url = `${itemsUrl(auth.gatewayId)}?verseId=${encodeURIComponent(auth.verseId)}&page=${page}&limit=${limit}&includeArchived=false`;
    const res = await fetch(url, { headers: headers(auth.token) });
    if (!res.ok) {
      throw new Error(`상품 목록 조회 실패 (${res.status}): ${await res.text()}`);
    }
    const body = (await res.json()) as {
      items?: VxServerItem[];
      data?: VxServerItem[];
      total?: number;
      meta?: { total?: number };
    };
    const items = body.items ?? body.data ?? [];
    if (items.length === 0) break;
    for (const it of items) {
      if (it.productId) map.set(it.productId, it);
    }
    const total = body.total ?? body.meta?.total;
    // >= : productId 없는 항목은 skip 되므로 map.size 가 total 보다 작을 수 있음.
    if (total != null && map.size >= total) break;
    if (items.length < limit) break;
    await sleep(LIST_PAGE_DELAY_MS);
  }
  return map;
}

export async function createShopItem(auth: VxAuth, payload: VxUpsertPayload): Promise<void> {
  const res = await fetch(itemsUrl(auth.gatewayId), {
    method: 'POST',
    headers: headers(auth.token),
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    throw new Error(`생성 실패 ${payload.productId} (${res.status}): ${await res.text()}`);
}

export async function updateShopItem(
  auth: VxAuth,
  serverItemId: string | number,
  payload: VxUpsertPayload
): Promise<void> {
  const body = {
    productId: payload.productId,
    name: payload.name,
    price: payload.price,
    stock: payload.stock,
    // imageUrl 필수 — 누락 시 기존 상품 이미지가 라이브 shop 에 영원히 갱신되지 않는다.
    imageUrl: payload.imageUrl,
    description: payload.description,
    metadata: payload.metadata,
    isActive: payload.isActive,
    purchaseConstraints: payload.purchaseConstraints,
  };
  const res = await fetch(`${itemsUrl(auth.gatewayId)}/${serverItemId}`, {
    method: 'PUT',
    headers: headers(auth.token),
    body: JSON.stringify(body),
  });
  if (!res.ok)
    throw new Error(`갱신 실패 ${payload.productId} (${res.status}): ${await res.text()}`);
}

/**
 * 단일 sales 페이지를 bounded retry 로 페치.
 * 429/5xx 는 일시 오류로 보고 백오프 후 재시도(최대 SALES_MAX_RETRIES); 그 외 4xx 는 즉시 throw.
 */
async function fetchSalesPageWithRetry(auth: VxAuth, page: number): Promise<Response> {
  const url = `${salesUrl(auth.gatewayId)}?verseId=${encodeURIComponent(auth.verseId)}&page=${page}&limit=${SALES_PAGE_LIMIT}`;
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(url, { headers: headers(auth.token) });
    if (res.ok) return res;
    const retryable = res.status === 429 || res.status >= 500;
    if (retryable && attempt < SALES_MAX_RETRIES) {
      await sleep(SALES_RETRY_BACKOFF_MS[attempt] ?? SALES_RETRY_BACKOFF_MS.at(-1) ?? 1000);
      continue;
    }
    throw new Error(`판매 조회 실패 (page=${page}, ${res.status}): ${await res.text()}`);
  }
}

export interface FetchSalesOptions {
  /**
   * 조회 기간 시작 시각(ms). 지정하면 이 시각보다 오래된 레코드가 나온 페이지에서 순회를 멈춘다.
   * null/미지정이면 마지막 페이지까지 전량 페치.
   */
  sinceMs?: number | null;
}

/**
 * 선택 verse 의 판매 레코드를 페이지 순회로 페치. (캐시 없음 — 매 호출마다 다시 순회)
 *
 * sales API 는 newest-first(id 내림차순) 로 응답하므로 sinceMs 를 주면 최신 페이지부터 읽다가
 * 기간 밖 레코드를 만난 페이지에서 멈춘다(analytics-worker walkSales 와 같은 전제).
 * 정렬은 계약이 아니라 관측이라 순회 중 내림차순 여부를 계속 검증하고, 한 번이라도 깨지면
 * 조기 종료를 포기하고 끝까지 걷는다(호출 절약보다 누락 방지 우선).
 */
export async function fetchAllSales(
  auth: VxAuth,
  onProgress?: (p: { page: number; total: number }) => void,
  opts: FetchSalesOptions = {}
): Promise<VxSale[]> {
  const since = opts.sinceMs ?? null;
  const out: VxSale[] = [];
  let sortedDescending = true;
  let prevId = Number.POSITIVE_INFINITY;
  for (let page = 1; page < MAX_SALES_PAGES; page++) {
    const res = await fetchSalesPageWithRetry(auth, page);
    // extractSales 는 SaleRecord[]; VxSale 은 index signature 가 없어 직접 캐스트 불가 → unknown 경유.
    const sales = extractSales(await res.json()) as unknown as VxSale[];
    if (sales.length === 0) break;
    let hitOld = false;
    for (const s of sales) {
      if (s.id >= prevId) sortedDescending = false; // 페이지 내부·경계 모두 검증
      prevId = s.id;
      if (since != null) {
        const t = Date.parse(s.createdAt);
        // 파싱 불가한 createdAt 은 판정에서 제외 — 잘못된 지점에서 멈추지 않도록.
        if (!Number.isNaN(t) && t < since) hitOld = true;
      }
      out.push(s);
    }
    onProgress?.({ page, total: out.length });
    if (since != null && sortedDescending && hitOld) break; // 기간 경계를 넘었다
    if (sales.length < SALES_PAGE_LIMIT) break; // 마지막 페이지
    await sleep(SALES_PAGE_DELAY_MS);
  }
  return out;
}
